/**
 * LocalizedTextField Component
 *
 * One text of the generated-document settings, edited per output language behind a cs/en switch.
 */
import { useState } from 'react';
import { ExpandableTextarea, Icon, InputWrapper, TabSwitcher } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import {
  OUTPUT_LOCALES,
  printedFallback,
  type LocalizedDraft,
  type OutputLocale
} from './use-document-output-section';

interface LocalizedTextFieldProps {
  readonly label: string;
  readonly hint: string;
  readonly value: LocalizedDraft;
  /** Example wording in the given language; nothing is filled in for the user. */
  readonly placeholder: (locale: OutputLocale) => string;
  readonly maxLength: number;
  /** Visible rows before the field scrolls; the modal editor takes longer text. */
  readonly rows?: number;
  /** Whether the text in a language is over `maxLength`, so a hidden language can say so too. */
  readonly isTooLong: (locale: OutputLocale) => boolean;
  readonly disabled?: boolean;
  readonly testId: string;
  onChange(locale: OutputLocale, text: string): void;
}

export function LocalizedTextField({
  label,
  hint,
  value,
  placeholder,
  maxLength,
  rows = 4,
  isTooLong,
  disabled = false,
  testId,
  onChange
}: LocalizedTextFieldProps) {
  const t = useFeatureTranslation('systemSettings.documentOutput');
  const [locale, setLocale] = useState<OutputLocale>('cs');

  // Every language over the limit is named, whichever tab is open.
  const tooLongMessages = OUTPUT_LOCALES.filter(isTooLong).map(tooLong =>
    t(`tooLong.${tooLong}`, { max: maxLength })
  );
  const errorText = tooLongMessages.length > 0 ? tooLongMessages.join(' ') : null;
  const fallback = printedFallback(value, locale);

  return (
    <div data-testid={testId}>
      <InputWrapper
        label={label}
        hint={hint}
        htmlFor={`${testId}-input`}
        isError={errorText !== null}
        errorText={errorText}
        className='gap-xs'
      >
        <TabSwitcher
          items={OUTPUT_LOCALES.map(id => ({
            id,
            label: t(`locales.${id}`),
            badge: isTooLong(id) ? (
              <Icon
                name='triangle-exclamation'
                className='h-3.5 w-3.5 text-error-text'
                aria-hidden='true'
              />
            ) : undefined
          }))}
          activeTab={locale}
          onTabChange={setLocale}
          className='w-fit'
          testId={`${testId}-locales`}
        />
        <ExpandableTextarea
          id={`${testId}-input`}
          label={`${label} · ${t(`locales.${locale}`)}`}
          rows={rows}
          value={value[locale]}
          placeholder={placeholder(locale)}
          disabled={disabled}
          errorText={isTooLong(locale) ? t(`tooLong.${locale}`, { max: maxLength }) : null}
          onChange={next => onChange(locale, next)}
        />
      </InputWrapper>
      {fallback && (
        <p
          className='mt-xs text-sm text-foreground-muted whitespace-pre-line break-words line-clamp-3'
          data-testid={`${testId}-fallback`}
        >
          <span className='font-medium'>{t(`printsOther.${fallback.locale}`)}:</span>{' '}
          {fallback.text}
        </p>
      )}
    </div>
  );
}
